import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import SectionNav from "../components/SectionNav";
import { PageHero, SectionHead, CtaBand } from "../components/ui";
import { MediaForm } from "../components/forms";
import { MEDIA_EMAIL } from "../data/site";

const TABS = [
    { id: "press", label: "Press Releases", empty: "No press releases published yet.", note: "Official AddiGen announcements will appear here, each with publication date, category, excerpt and full text." },
    { id: "company", label: "Company News", empty: "No company news published yet.", note: "Updates from across the development platform — milestones, appointments and project progress — will appear here." },
];

const NEWS_SECTIONS = [
    { id: "newsroom", label: "Newsroom" },
    { id: "media-enquiries", label: "Media enquiries" },
];

export default function News() {
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get("tab") === "company" ? "company" : "press");

    useEffect(() => {
        const t = searchParams.get("tab");
        if (t === "press" || t === "company") setTab(t);
    }, [searchParams]);

    const active = TABS.find((t) => t.id === tab);
    return (
        <>
            <PageHero
                eyebrow="News & Media"
                lines={["News, statements", "and media enquiries."]}
                trail={[{ label: "Home", path: "/" }, { label: "News & Media" }]}
            />
            <SectionNav items={NEWS_SECTIONS} navKey="news" />
            <section className="bg-paper">
                <div className="container-x py-20 sm:py-24 lg:py-28">
                    <div id="newsroom" className="scroll-mt-36">
                        <Reveal>
                            <div role="tablist" aria-label="News categories" className="flex gap-px border border-line bg-line">
                                {TABS.map((t) => (
                                    <button
                                        key={t.id}
                                        role="tab"
                                        aria-selected={tab === t.id}
                                        onClick={() => setTab(t.id)}
                                        data-testid={`news-tab-${t.id}`}
                                        className={`flex-1 px-6 py-4 font-display text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                                            tab === t.id ? "bg-navy text-white" : "bg-white text-ink-2 hover:text-blue"
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div role="tabpanel" className="mt-10" data-testid={`news-panel-${tab}`}>
                                <div className="border border-dashed border-line-2 bg-white px-8 py-16 text-center sm:px-12 sm:py-20" data-testid="news-empty-state">
                                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue">{active.empty}</p>
                                    <p className="body-lg mx-auto mt-5 max-w-lg">{active.note}</p>
                                    <p className="body-sm mt-8 text-ink-3">
                                        For media enquiries, contact{" "}
                                        <a href={`mailto:${MEDIA_EMAIL}`} className="text-blue underline underline-offset-4">
                                            {MEDIA_EMAIL}
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                    <div id="media-enquiries" className="mt-20 grid scroll-mt-36 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <SectionHead
                                eyebrow="Media enquiries"
                                title="Speaking to media, on the record."
                            />
                            <Reveal><p className="body-lg mt-6 max-w-md">Journalists and publications can reach the AddiGen team directly. We respond to enquiries with a stated deadline as a priority.</p></Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-8 border-l-2 border-blue pl-5 font-mono text-[12px] uppercase tracking-[0.16em] text-ink-2">
                                    media@addigen.com
                                </p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-7">
                            <Reveal delay={0.05}>
                                <div className="border border-line bg-white p-8 sm:p-10">
                                    <MediaForm />
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>
            <CtaBand cta="Contact us" />
        </>
    );
}
